// In-memory cache of CF-minted signed URLs for audio + art.

import firebase from './firebaseConfig';

const REFRESH_LEAD_TIME_MS = 5 * 60 * 1000;
const DEFAULT_AUDIO_TTL_MS = 60 * 60 * 1000;
const DEFAULT_ART_TTL_MS = 24 * 60 * 60 * 1000;

const audioCache = new Map();
const artCache = new Map();

const inflightAudio = new Map();
const inflightArt = new Map();

function isFresh(entry) {
    if (!entry) return false;
    return entry.expiresAt - Date.now() > REFRESH_LEAD_TIME_MS;
}

function audioCacheKey({ kind, contentId }) {
    return `${kind}:${contentId}`;
}

function parseExpiresAt(raw, defaultTTL) {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw;
    }
    return Date.now() + defaultTTL;
}

export function cachedAudioURL(args) {
    const entry = audioCache.get(audioCacheKey(args));
    return isFresh(entry) ? entry.url : null;
}

export function cachedArtURL(artId) {
    const entry = artCache.get(artId);
    return isFresh(entry) ? entry.url : null;
}

export async function resolveAudioURL({ artistId, projectId, kind, contentId }) {
    const key = audioCacheKey({ kind, contentId });
    const cached = audioCache.get(key);
    if (isFresh(cached)) return cached.url;

    const inflight = inflightAudio.get(key);
    if (inflight) return inflight;

    const promise = (async () => {
        const callable = firebase.functions().httpsCallable('getGranteeContentAudioURL');
        const result = await callable({ artistId, projectId, kind, contentId });
        const data = result?.data || {};
        if (!data.url) throw new Error('No url in CF response');
        audioCache.set(key, {
            url: data.url,
            expiresAt: parseExpiresAt(data.expiresAt, DEFAULT_AUDIO_TTL_MS),
        });
        return data.url;
    })().finally(() => {
        inflightAudio.delete(key);
    });

    inflightAudio.set(key, promise);
    return promise;
}

// `trackId` is optional context for track-only grantees: pass it from
// callsites where the user is viewing a specific track so the CF can
// authorize via track-grant tier even when the user has no project grant.
export async function resolveArtURL({ artistId, projectId, artId, trackId }) {
    const cached = artCache.get(artId);
    if (isFresh(cached)) return cached.url;

    const inflight = inflightArt.get(artId);
    if (inflight) return inflight;

    const promise = (async () => {
        const callable = firebase.functions().httpsCallable('getContentArtURL');
        const payload = { artistId, projectId, contentId: artId };
        if (trackId) payload.trackId = trackId;
        const result = await callable(payload);
        const data = result?.data || {};
        if (!data.url) throw new Error('No url in CF response');
        artCache.set(artId, {
            url: data.url,
            expiresAt: parseExpiresAt(data.expiresAt, DEFAULT_ART_TTL_MS),
        });
        return data.url;
    })().finally(() => {
        inflightArt.delete(artId);
    });

    inflightArt.set(artId, promise);
    return promise;
}

export function invalidateAudio({ kind, contentId }) {
    audioCache.delete(audioCacheKey({ kind, contentId }));
}

export function invalidateArt(artId) {
    artCache.delete(artId);
}
